import {
  compact,
  every,
  filter,
  forEach,
  get,
  isEmpty,
  keys,
  map,
  negate,
  reduce,
  values
} from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import Dashboard from "../../../components/Dashboard";
import FilterLabels from "../../../components/labels/FilterLabels";
import { useBillingPlans } from "../../../context/billingPlans";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useLabels } from "../../../context/labels";
import {
  LoadingKey,
  useLoading
} from "../../../context/loading/LoadingProvider";
import { useMembers } from "../../../context/members/MembersProvider";
import { useToast } from "../../../context/toast";
import { useTransmitScales } from "../../../context/transmitScales";
import { useWallets } from "../../../context/wallets";
import { useWorkspaces } from "../../../context/workspaces";
import { Entity } from "../../../utils/constants";
import { getClassification } from "../../../utils/hotspotUtils";
import { PhpHotspot, PlanType } from "../../../utils/types";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import { isActiveInstall } from "../InstallsData/utils";
import LockedWorkspace from "../LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../LockedWorkspaceByMembers";
import WalletsSection from "./WalletsSection";

interface Props extends RouteComponentProps<{}> {}

const MyDashboardPage: React.FC<Props> = () => {
  const { getIsReady } = useLoading();
  const {
    workspaceHotspots,
    fetchRewardsByTypeByHotspots,
    fetchWitnessesCountsByHotspots,
    toggleAddHotspotSidebar
  } = useHotspots();
  const { membersLimitExceeded } = useMembers();
  const {
    currentWorkspaceIsLocked,
    currentWorkspace,
    currentWorkspaceAllowedForCommunityPlan
  } = useWorkspaces();
  const { calculatedTransmitScales, isTransmitScaleActive } =
    useTransmitScales();
  const { setModalUpgradeConfig } = useToast();
  const { installs } = useInstalls();
  const { labels, linkedLabels } = useLabels();
  const { setPaymentStep, getPlanByType, plans, setNewPlan } =
    useBillingPlans();
  const { wallets } = useWallets();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const history = useHistory();

  useEffect(() => {
    if (!currentWorkspace) return;

    if (currentWorkspace.active_plan?.type !== PlanType.community) {
      return;
    }

    if (
      currentWorkspace.counts?.hotspot <=
      currentWorkspace.active_plan?.allowed_hotspots
    ) {
      return;
    }

    setModalUpgradeConfig({
      open: true,
      nextPlan: getPlanByType(PlanType.pro),
      resolve: () => {
        setNewPlan((prevState) => ({
          ...prevState,
          plan: getPlanByType(PlanType.pro)
        }));
      }
    });
  }, [
    currentWorkspace,
    getPlanByType,
    history,
    plans,
    setModalUpgradeConfig,
    setNewPlan,
    setPaymentStep
  ]);

  useEffect(() => {
    const workspaceHotspotIds = map(workspaceHotspots, "id");

    if (
      !isEmpty(workspaceHotspotIds) &&
      currentWorkspace?.permissions["rewards-by-type"]
    ) {
      fetchRewardsByTypeByHotspots(workspaceHotspotIds);
    }

    if (!isEmpty(workspaceHotspotIds)) {
      fetchWitnessesCountsByHotspots(workspaceHotspotIds); // Needed to calculate the witness reward units
    }
  }, [
    currentWorkspace?.permissions,
    fetchRewardsByTypeByHotspots,
    fetchWitnessesCountsByHotspots,
    workspaceHotspots
  ]);

  const isHotspotInstalled = useCallback(
    ({ id }: PhpHotspot) => {
      const linkedActiveInstalls = filter(
        values(installs).filter(isActiveInstall),
        { hotspot_uuid: id }
      );

      return !isEmpty(linkedActiveInstalls);
    },
    [installs]
  );

  const smartLabelFilters: {
    [key: string]: (hotspot: PhpHotspot) => boolean;
  } = useMemo(
    () => ({
      active: ({ online }) => online,
      inactive: ({ online }) => !online,
      "active install": isHotspotInstalled,
      "no active install": negate(isHotspotInstalled),
      activated: ({ id }) => isTransmitScaleActive(id),
      ...reduce(
        [
          "excellent",
          "good",
          "mediocre",
          "poor",
          "terrible",
          "inactive",
          "disabled"
        ],
        (
          acc: {
            [key: string]: (hotspot: PhpHotspot) => boolean;
          },
          classification
        ) => {
          acc[`${classification} transmit scale`] = ({ id }) => {
            const isActive = isTransmitScaleActive(id);
            const locationClassification = isActive
              ? getClassification(get(calculatedTransmitScales, id, 0))
              : "disabled";
            return classification === locationClassification;
          };

          return acc;
        },
        {}
      )
    }),
    [isHotspotInstalled, isTransmitScaleActive, calculatedTransmitScales]
  );

  const allFilteredLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.workspaceDashboardHotspots],
        labels,
        smartLabelFilters,
        Entity.hotspot
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredHotspots = useMemo(() => {
    const filteredHotspotLabels = filter(allFilteredLabels, {
      type: Entity.hotspot
    });
    const filteredWalletLabels = filter(allFilteredLabels, {
      type: Entity.wallet
    });

    const filteredWalletUuids: string[] = [];

    forEach(linkedLabels, (linkedLabelUuids, walletUuid) => {
      filteredWalletLabels.forEach(({ uuid }) => {
        if (linkedLabelUuids.includes(uuid)) {
          filteredWalletUuids.push(walletUuid);
        }
      });
    });

    const filteredWalletAddresses = compact(
      filteredWalletUuids.map((walletUuid) => wallets[walletUuid]?.address)
    );

    let filteredList = workspaceHotspots.filter((hotspot) => {
      if (
        !isEmpty(filteredHotspotLabels) &&
        !every(filteredHotspotLabels, ({ uuid }) => {
          const smartFilterFunction = (smartLabelFilters as any)[uuid]; // TODO: fix typing

          return smartFilterFunction
            ? smartFilterFunction(hotspot)
            : (linkedLabels[hotspot.id] || []).includes(uuid);
        })
      ) {
        return false;
      }

      if (
        !isEmpty(filteredWalletAddresses) &&
        !filteredWalletAddresses.includes(hotspot.owner)
      ) {
        return false;
      }

      return true;
    });

    return filteredList;
  }, [
    allFilteredLabels,
    linkedLabels,
    smartLabelFilters,
    wallets,
    workspaceHotspots
  ]);

  const isLoading = useMemo(
    () =>
      !getIsReady([LoadingKey.workspaceWallets, LoadingKey.workspaceHotspots]),
    [getIsReady]
  );

  if (currentWorkspaceIsLocked) {
    return (
      <LockedWorkspace
        title="Payment failed"
        paragraphs={[
          "Your subscription payment failed. Please resolve the issue with your payment method."
        ]}
        buttonText="Update payment details"
        buttonLink="/workspaces/settings/billing"
      />
    );
  }

  if (!currentWorkspaceAllowedForCommunityPlan) {
    return <LockedWorkspaceByCommunityPlan />;
  }

  if (membersLimitExceeded) {
    return <LockedWorkspaceByMembers />;
  }

  if (isLoading) {
    return <CenteredLoading className="sm:h-full h-96" />;
  }

  return (
    <>
      <main
        className="flex-1 relative z-0 overflow-y-auto focus:outline-none"
        tabIndex={0}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <WalletsSection />

          <div className="mt-6 pb-5 border-b border-gray-200 dark:border-gray-500">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
              <span className="flex-1">Hotspots</span>
              <button
                type="button"
                className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
                onClick={() => toggleAddHotspotSidebar()}
              >
                Add hotspot
              </button>
            </h3>
          </div>

          {isEmpty(workspaceHotspots) ? (
            <div className="mt-6 dark:text-gray-300">
              Add hotspots to your workspace to get an overview of their
              performance.
            </div>
          ) : (
            <>
              <FilterLabels
                className="mt-4"
                entity={Entity.hotspot}
                filteredLabels={allFilteredLabels}
                smartLabels={keys(smartLabelFilters).map((key) =>
                  getSmartLabelFromKey(key, Entity.hotspot)
                )}
                filterKey={LabelFilterKey.workspaceDashboardHotspots}
              />
              <Dashboard
                hotspots={filteredHotspots}
                gridColsInDesktop={3}
                withExtraRewardSummaries
                withRewardsByType
                historyRewardsWithoutToday={false}
              />
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default MyDashboardPage;
